import React, { Fragment, useCallback, useEffect, type ComponentType, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { IconButton, type IconButtonProps } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import MinimizeIconNew from '@atlaskit/icon/core/minimize';
import ShrinkDiagonalIcon from '@atlaskit/icon/core/shrink-diagonal';
import { ModalHeader as AKModalHeader, ModalTitle as AKModalTitle } from '@atlaskit/modal-dialog';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { CreateWorkVideoEducationModalTriggerButtonBanner } from '@atlassian/jira-issue-create-commons/src/ui/create-work-video-education-modal-trigger-button/index.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	BANNER,
} from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { composeTraitsSelectors } from '@atlassian/jira-traits/src/TraitsStore.tsx';
import TraitsConditionalRenderer from '@atlassian/jira-traits/src/TraitsConditionalRenderer.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import {
	CORE_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useEnterFocusState } from '../../../controllers/enter-focus-state/index.tsx';
import { useMinimizableModal } from '../../../controllers/use-minimizable-modal/index.tsx';
import { TransitionState } from '../../../controllers/use-minimizable-modal/types.tsx';
import { useNestedModalDetector } from '../../../controllers/use-nested-modal-detector/index.tsx';
import type { MinimizableModalView, ModalHeaderProps } from '../../../types.tsx';
import { usePassesIPHTargetingTraitsSelector } from '../../../traits/iph-experiment-traits.tsx';
import messages from './messages.tsx';
import type { ViewChangerProps } from './types.tsx';

export const ViewChanger = ({
	isMini = false,
	ViewChangerContainer,
	dismissModalEntryPointWrapper,
	meatBallMenu,
}: ViewChangerProps) => {
	const {
		view,
		setView,
		onClose,
		isTransitionInState,
		setShouldFocusChanger,
		displayMinimizableButtons,
	} = useMinimizableModal();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { minimizedButtonRef, modalButtonRef, miniButtonRef, wrapperRef, onKeyDown } =
		useEnterFocusState();

	const onUpdateView = useCallback(
		(newView: MinimizableModalView) => {
			if (!isTransitionInState([view], TransitionState.ENTERED)) return;
			setShouldFocusChanger(true);
			setView(newView);
			const analyticsEvent = createAnalyticsEvent({
				action: 'button clicked',
				actionSubject: newView,
			});
			fireUIAnalytics(analyticsEvent, {
				source: 'globalIssueCreateModal',
				previousGICViewState: view,
			});
		},
		[isTransitionInState, view, setShouldFocusChanger, setView, createAnalyticsEvent],
	);

	const onViewChangeToMinimized = useCallback(() => onUpdateView('minimized'), [onUpdateView]);
	const onViewChangeToMini = useCallback(() => onUpdateView('mini'), [onUpdateView]);
	const onViewChangeToModal = useCallback(() => onUpdateView('modal'), [onUpdateView]);

	const { shouldDisableViewChangerButtons, customDisabledViewChangerButtonsMessage } =
		useMinimizableModal();

	useEffect(() => {
		if (shouldDisableViewChangerButtons) {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'disabled',
					actionSubject: 'view changer buttons',
				}),
			);
		}
	}, [
		createAnalyticsEvent,
		customDisabledViewChangerButtonsMessage,
		shouldDisableViewChangerButtons,
	]);

	useNestedModalDetector();

	const Wrapper = ViewChangerContainer || Fragment;

	const expandMessage = messages.fullScreen;
	const dockMessage = messages.fullScreenExit;
	const dockDisabledMessage = messages.fullScreenExitDisabled;
	const tooltipOptions: IconButtonProps['tooltip'] = {
		position: 'top',
		hideTooltipOnClick: true,
	};

	let closeBtn;

	if (dismissModalEntryPointWrapper) {
		const DismissModalEntryPointWrapper = dismissModalEntryPointWrapper;
		closeBtn = (
			<DismissModalEntryPointWrapper>
				<IconButton
					isTooltipDisabled={false}
					tooltip={tooltipOptions}
					icon={CrossIcon}
					label={formatMessage(messages.close)}
					onClick={onClose}
					testId="minimizable-modal.ui.modal-container.modal-header.close-button"
					appearance={isMini && isVisualRefreshEnabled() ? 'default' : 'subtle'}
				/>
			</DismissModalEntryPointWrapper>
		);
	} else {
		closeBtn = (
			<IconButton
				isTooltipDisabled={false}
				tooltip={tooltipOptions}
				icon={CrossIcon}
				label={formatMessage(messages.close)}
				onClick={onClose}
				testId="minimizable-modal.ui.modal-container.modal-header.close-button"
				appearance={isMini && isVisualRefreshEnabled() ? 'default' : 'subtle'}
			/>
		);
	}

	return (
		<Wrapper>
			<ViewChangerWrapper
				tabIndex={-1}
				ref={wrapperRef}
				isMini={isMini}
				data-testid="minimizable-modal.ui.modal-container.modal-header.view-changer-wrapper"
			>
				<Inline space="space.100" as="ul" xcss={headerButtonsListStyles}>
					{!displayMinimizableButtons ? null : (
						<>
							<Box as="li" xcss={lineItemStyles}>
								<IconButton
									isTooltipDisabled={false}
									tooltip={tooltipOptions}
									icon={MinimizeIconNew}
									label={
										shouldDisableViewChangerButtons
											? customDisabledViewChangerButtonsMessage ||
												formatMessage(messages.minimizeDisabled)
											: formatMessage(messages.minimize)
									}
									onClick={onViewChangeToMinimized}
									testId="minimizable-modal.ui.modal-container.modal-header.minimized-button"
									appearance={isMini && isVisualRefreshEnabled() ? 'default' : 'subtle'}
									onKeyDown={onKeyDown}
									isDisabled={shouldDisableViewChangerButtons}
									ref={minimizedButtonRef}
								/>
							</Box>
							{!isMini && (
								<Box as="li" xcss={lineItemStyles}>
									<IconButton
										isTooltipDisabled={false}
										tooltip={tooltipOptions}
										icon={ShrinkDiagonalIcon}
										label={
											shouldDisableViewChangerButtons
												? customDisabledViewChangerButtonsMessage ||
													formatMessage(dockDisabledMessage)
												: formatMessage(dockMessage)
										}
										onClick={onViewChangeToMini}
										testId="minimizable-modal.ui.modal-container.modal-header.mini-button"
										appearance={isMini && isVisualRefreshEnabled() ? 'default' : 'subtle'}
										onKeyDown={onKeyDown}
										isDisabled={shouldDisableViewChangerButtons}
										ref={miniButtonRef}
									/>
								</Box>
							)}
							{isMini && (
								<Box as="li" xcss={lineItemStyles}>
									<IconButton
										isTooltipDisabled={false}
										tooltip={tooltipOptions}
										icon={GrowDiagonalIcon}
										label={formatMessage(expandMessage)}
										onClick={onViewChangeToModal}
										testId="minimizable-modal.ui.modal-container.modal-header.modal-button"
										appearance={isMini && isVisualRefreshEnabled() ? 'default' : 'subtle'}
										onKeyDown={onKeyDown}
										ref={modalButtonRef}
									/>
								</Box>
							)}
							{meatBallMenu && (
								<Box as="li" xcss={lineItemStyles}>
									{meatBallMenu}
								</Box>
							)}
							<Box as="li" xcss={lineItemStyles}>
								{closeBtn}
							</Box>
						</>
					)}
				</Inline>
			</ViewChangerWrapper>
		</Wrapper>
	);
};

export const ModalHeader: ComponentType<ModalHeaderProps> = ({
	children,
	ViewChangerContainer,
	dismissModalEntryPointWrapper,
	meatBallMenu,
	project,
	...rest
}) => {
	const locale = useLocale();
	const isEnglishLocale = locale && typeof locale === 'string' && locale.startsWith('en');
	const isSoftwareOrBusinessProject =
		project?.projectType === SOFTWARE_PROJECT || project?.projectType === CORE_PROJECT;

	return (
		<HeaderWrapper {...rest}>
			<AKModalHeader>
				<AKModalTitle>{children}</AKModalTitle>
				<ViewChanger
					ViewChangerContainer={ViewChangerContainer}
					dismissModalEntryPointWrapper={dismissModalEntryPointWrapper}
					meatBallMenu={meatBallMenu}
				/>
			</AKModalHeader>
			{isSoftwareOrBusinessProject && isEnglishLocale && fg('jira_in_product_video_pilot_fg') && (
				<TraitsConditionalRenderer
					// eslint-disable-next-line react-hooks/react-compiler
					conditions={composeTraitsSelectors(['SITE_USER'], [usePassesIPHTargetingTraitsSelector])}
				>
					{expVal('jira_in_product_video_pilot_v1', 'isEnabled', false) && (
						<ContextualAnalyticsData sourceName="gicCreateWorkItemVideo" sourceType={BANNER}>
							<CreateWorkVideoEducationModalTriggerButtonBanner />
							<FireScreenAnalytics />
						</ContextualAnalyticsData>
					)}
				</TraitsConditionalRenderer>
			)}
		</HeaderWrapper>
	);
};

export const ModalTitle = (props: { children?: ReactNode; testId?: string }) => {
	const { view } = useMinimizableModal();
	const { formatMessage } = useIntl();

	const { children, testId } = props;

	if (view === 'mini' || view === 'minimized') {
		return (
			<Box
				as="h2"
				xcss={isVisualRefreshEnabled() ? headerStyleswithVR : headerStyles}
				testId={testId}
				id="minimizable-modal.modal-container.modal-header.title"
			>
				{children || formatMessage(messages.headerFallbackTitle)}
			</Box>
		);
	}

	return isVisualRefreshEnabled() ? (
		<Heading as="h2" size="medium" color="color.text">
			{children}
		</Heading>
	) : (
		<div {...props} />
	);
};

export const MiniHeader: ComponentType<ModalHeaderProps> = ({
	children,
	dismissModalEntryPointWrapper,
	...rest
}) => (
	<MiniHeaderWrapper {...rest}>
		{children || <ModalTitle />}
		<ViewChanger isMini dismissModalEntryPointWrapper={dismissModalEntryPointWrapper} />
	</MiniHeaderWrapper>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ViewChangerWrapper = styled.div<{ isMini: boolean }>(
	{
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-end',
		flexShrink: 0,
		'&:focus': {
			outline: 'none',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isMini
			? {
					paddingRight: token('space.050'),
				}
			: {},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	header: {
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingRight: token('space.200'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MiniHeaderWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'flex-end',
	color: token('color.text'),
	borderBottom: `0.5px solid ${token('color.border')}`,
	boxShadow: `0 6px 5px -5px ${token('elevation.shadow.overflow.spread')}`,
	marginTop: 0,
	marginRight: token('space.150'),
	marginBottom: 0,
	marginLeft: token('space.150'),
	paddingTop: token('space.250'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.250'),
	paddingLeft: token('space.100'),
});

// Atlaskit does not have a solution for using tooltips for disabled buttons.
// Workaround to allow tooltip on disabled buttons, technically a design anti-pattern
// The button must be wrapped in a div and the button must have pointer-events set to none when disabled.
// Setting the pointer-events to none sets the pointer to default state, it will look slightly different than before (accepted by design).

const headerButtonsListStyles = xcss({
	display: 'inline-flex',
	listStyleType: 'none',
	borderRadius: 'borderRadius.25',
});

const lineItemStyles = xcss({
	margin: 'auto',
});

const headerStyles = xcss({
	color: 'color.text',
	font: token('font.heading.xsmall'),
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	flexShrink: 1,
	flexGrow: 1,
	overflow: 'hidden',
	marginTop: '0',
});

const headerStyleswithVR = xcss({
	color: 'color.text',
	font: token('font.heading.small'),
	paddingRight: 'space.100',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	flexShrink: 1,
	flexGrow: 1,
	overflow: 'hidden',
	marginTop: '0',
});
